import { ReactComponent as GetApp } from 'resources/images/icon-dev-ic-get-app.svg';
import styled from 'styled-components/macro';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Modal, Button, ButtonEnums } from 'components/atoms';
import { personAccountCountrySelector } from 'modules/user/selectors';
import { MipCountry } from 'modules/user/constants';

const MIP_APPLICATION_URLS = {
  UK: '/resources/download/members-in-practice-mip-application-for-united-kingdom-including-northern-ireland-members',
  IRELAND: '/resources/download/members-in-practice-mip-application-for-republic-of-ireland-members',
  OTHER:
    '/resources/download/members-in-practice-mip-application-for-non-uk-and-non-republic-of-ireland-members',
};

interface Props {
  onClose(): void;
  isLocked?: boolean;
  open: boolean;
}

export const MipDownloadApplicationModal: React.FC<Props> = (props: Props) => {
  const { onClose } = props;
  const history = useHistory();
  const getCountryName = useSelector(personAccountCountrySelector);
  const isFromUK = getCountryName === MipCountry.UK;
  const isFromIreland = getCountryName === MipCountry.IRELAND;

  const handleDownloadClick = useCallback(() => {
    let url = MIP_APPLICATION_URLS.OTHER;
    if (isFromUK) {
      url = MIP_APPLICATION_URLS.UK;
    } else if (isFromIreland) {
      url = MIP_APPLICATION_URLS.IRELAND;
    }

    onClose();
    history.push(url);
  }, [isFromUK, isFromIreland, onClose, history]);

  return (
    <StyledModal
      open={props.open}
      onClose={props.onClose}
      heading="Download the application"
      testId="download-application-modal"
      showCloseCross
      cancelNode={
        <Button
          size={ButtonEnums.sizes.small}
          variant={ButtonEnums.variants.secondary}
          testId="cancel-download"
          onClick={props.onClose}
        >
          Cancel
        </Button>
      }
      confirmNode={
        <Button
          size={ButtonEnums.sizes.small}
          testId="start-download"
          variant={ButtonEnums.variants.primary}
          onClick={handleDownloadClick}
          icon={<GetApp />}
          iconPosition={ButtonEnums.iconPosition.left}
          className="downloadButton"
        >
          Download
        </Button>
      }
    >
      Ensure you have all the documentation ready before you begin. After completing this application, please save your
      completed documents and email them to MiP.Applications@aicpa-cima.com.{' '}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  &&&& > button {
    position: absolute !important;
    top: ${props => props.theme.pxToRem(16)}!important;
    right: ${props => props.theme.pxToRem(16)}!important;
  }

  &&&& {
    width: ${props => props.theme.pxToRem(590)};
    background: ${props => props.theme.colors.neutralWhite};
    margin-top: ${props => props.theme.pxToRem(48)};
    border-radius: ${props => props.theme.pxToRem(2)};
    padding: ${props => props.theme.pxToRem(37)} 0 ${props => props.theme.pxToRem(20)}!important;
    box-sizing: border-box;

    ${props => props.theme.mediaQueries.mobileOnly} {
      width: 90%;
      margin-left: auto;
      margin-right: auto;
    }

    > .content {
      margin: 0 !important;
      text-align: center;
      box-sizing: border-box;
      padding: ${props => props.theme.pxToRem(30)} ${props => props.theme.pxToRem(20)}!important;
    }

    > .header {
      margin: 0 auto;
      text-align: center;
      display: table;
      h2 {
        margin: 0 auto;
        font-size: ${props => props.theme.fontSizes.l};
      }
    }

    > .actions {
      margin: 0 ${props => props.theme.pxToRem(20)};
    }

    > .actions > .downloadButton {
      width: ${props => props.theme.pxToRem(215)}!important;
    }
  }

  button > svg {
    fill: ${props => props.theme.colors.primaryDarkPurple};
  }
`;
